<template>
  <div>
    <PTable
      :columns="columns"
      :rows="permissions"
      :pagination="{ rowsPerPage: permissions?.length }"
      :hide-pagination="true"
      v-bind="conditionalProps"
    >
      <template #header="headerProps">
        <q-tr :props="headerProps">
          <q-th
            v-for="col in headerProps.cols"
            :key="col.name"
            :props="headerProps"
          >
            {{ col.label }}
          </q-th>
          <q-th>
            <div v-if="showSelectAllReset" class="flex justify-end">
              <PBtn
                label="Select All"
                dense
                flat
                class="q-mr-xl"
                @click="selectAll"
              />
              <PBtn label="Reset" dense flat @click="reset" />
            </div>
          </q-th>
        </q-tr>
      </template>

      <template #body="slotProps">
        <q-tr :props="slotProps">
          <q-td class="align-top">
            <div class="q-pt-sm">{{ slotProps.row.usecase.name }}</div>
          </q-td>
          <q-td colspan="2">
            <div :style="permissionsGridStyle" class="permissions-grid">
              <div
                v-for="permission in slotProps.row.permissions"
                :key="permission.code"
              >
                <PCheckbox
                  v-model="permission.allowed"
                  :color="
                    getHighlightColor(permission, slotProps.row.usecase.code)
                  "
                  :disable="formState.view"
                  keep-color
                  :label="permission.name"
                />
              </div>
            </div>
          </q-td>
        </q-tr>
      </template>
    </PTable>
  </div>
</template>

<script setup>
const props = defineProps({
  initialPermissions: {
    type: Array,
    default: () => [],
  },
  rolePermissions: {
    type: Array,
    default: () => [],
  },
  highlightChanges: {
    type: Boolean,
    default: false,
  },
  showSelectAllReset: {
    type: Boolean,
    default: false,
  },
});

const { formState } = storeToRefs(useRoleManagementStore());

const permissions = defineModel({ type: Array });

const columns = ref([
  {
    name: "page",
    required: true,
    label: "Page",
    align: "left",
    field: (row) => row.usecase.name,
  },
  {
    name: "permissions",
    required: true,
    label: "Permissions",
    align: "left",
    field: (row) => row.permissions,
  },
]);

const selectAll = () => {
  permissions.value = permissions.value.map((page) => {
    page.permissions = page.permissions.map((permission) => {
      permission.allowed = true;

      return permission;
    });

    return page;
  });
};

const reset = () => {
  permissions.value = structuredClone(toRaw(unref(props.initialPermissions)));
};

const conditionalProps = computed(() => {
  return props.showSelectAllReset
    ? {}
    : { flat: true, bordered: true, class: "q-ma-md" };
});

const getHighlightColor = (permission, useCaseCode) => {
  if (!props.highlightChanges) {
    return "";
  }

  const initialUsecase = props.rolePermissions.find(
    (initial) => initial.usecase.code === useCaseCode,
  );

  if (!initialUsecase) {
    return "";
  }

  const initialPermission = initialUsecase.permissions.find(
    (perm) => perm.code === permission.code,
  );

  if (initialPermission && initialPermission.allowed !== permission.allowed) {
    return "info-60";
  }

  return "";
};

const permissionsGridStyle = computed(() => {
  const minmaxValue = props.showSelectAllReset
    ? "minmax(90px, 1fr)"
    : "minmax(70px, 1fr)";

  return {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, ${minmaxValue})`,
    gap: "1rem",
    margin: "0.6rem 0 0.6rem 0",
  };
});
</script>

<style lang="scss" scoped>
:deep(.q-table) {
  table-layout: fixed;
}
</style>
